import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import './EditProfile.css';
import { deleteProfile, getProfile, updateProfile } from '../utils/api';

const EditProfile: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await getProfile(); // call backend
        const data = response.data;
  
        setUsername(data.username);
        setEmail(data.email);
        setPhoneNumber(data.phone_number || '');
        if (data.profile_picture) {
          // already a full URL (or backwards-compat filename)
          const pic = /^https?:\/\//i.test(data.profile_picture)
            ? data.profile_picture                       // Cloudinary
            : `http://localhost:5000/static/uploads/${data.profile_picture}`; // old files
          setPreviewUrl(pic);
        }        
      } catch (err) {
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
  
    loadProfile();
  }, []);
  

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
  
    const formData = new FormData();
    formData.append("username", username);
    formData.append("phone_number", phoneNumber);
    if (password) {
      formData.append("current_password", password);
      formData.append("new_password", confirmPassword);
    }
    if (profileImage) {
      formData.append("profile_picture", profileImage);
    }
  
    try {
      setSaving(true);
      setError("");
      await updateProfile(formData);
      setShowSuccessModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };
  

  const handleDelete = async () => {
    try {
      await deleteProfile();
      logout();
      navigate("/login");
    } catch (err) {
      setError("Failed to delete account");
    }
  };
  

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="edit-profile-container">
        <div className="container">
          <div className="edit-profile-content">
            <h1 className="page-title">EDIT PROFILE</h1>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="profile-form-container">
              <div className="profile-image-section">
                <div className="profile-image-container">
                  {previewUrl ? (
                    <img src={previewUrl || "/placeholder.svg"} alt="Profile" className="profile-image" />
                  ) : (
                    <div className="profile-image-placeholder">
                      <span>Upload Image</span>
                    </div>
                  )}
                </div>
                
                <div className="upload-profile-container">
                  <h3>Upload Profile Pictures</h3>
                  <div className="upload-area">
                    <p>Drag and drop your file(s) to upload from your computer</p>
                    <input
                      type="file"
                      id="profileImage"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="file-input"
                    />
                    <button type="button" className="cancel-upload-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="profile-details-section">
                <form onSubmit={handleSave}>
                  <div className="form-group">
                    <label htmlFor="username">Username:</label>
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email:</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phoneNumber">Phone number:</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>
                  
                  <div className="profile-actions">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="delete-account-btn"
                    >
                      Delete
                    </button>
                    <button
                      type="submit"
                      className="save-profile-btn"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            
            {showSuccessModal && (
              <div className="modal-overlay">
                <div className="success-modal">
                  <div className="success-icon">✓</div>
                  <h3>Successfully Saved!</h3>
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      navigate('/');
                    }}
                    className="return-btn"
                  >
                    Return
                  </button>
                </div>
              </div>
            )}
            
            {showDeleteConfirm && (
              <div className="modal-overlay">
                <div className="confirm-modal">
                  <div className="warning-icon">!</div>
                  <h3>Are You Sure You Want to delete?</h3>
                  <div className="confirm-actions">
                    <button
                      onClick={handleDelete}
                      className="confirm-delete-btn"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="cancel-delete-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProfile;